import { defaultThinkingLabel, resolveThoughtLabel } from './thoughtLabels';
/** Default minimum time a transient thought line stays visible before replacing (~2s reduces early flicker). */
export const DEFAULT_PROGRESS_MIN_DWELL_MS = 2000;
export function applyProgressToBlockStreamState(prev, p) {
    const noProgressLineYet = prev.progressThoughtKey == null && prev.progressMessage == null;
    const sameThoughtKey = p.thought_key != null && p.thought_key === prev.progressThoughtKey;
    let progressMessage;
    if (noProgressLineYet) {
        progressMessage = defaultThinkingLabel();
    }
    else if (sameThoughtKey && prev.progressMessage) {
        progressMessage = prev.progressMessage;
    }
    else {
        progressMessage = resolveThoughtLabel(p.thought_key, p.message);
    }
    return Object.assign(Object.assign({}, prev), { progressPhase: p.phase, progressThoughtKey: p.thought_key || null, progressMessage, progressDetail: p.detail || null, progressAgent: p.agent || prev.agent });
}
/**
 * Coalesces rapid-fire progress thoughts: each visible line stays at least
 * {@link minDwellMs}; bursts collapse to the latest payload when the window elapses.
 */
export class ProgressThoughtCoalescer {
    constructor(minDwellMs = DEFAULT_PROGRESS_MIN_DWELL_MS) {
        this.lastShownAt = 0;
        this.pending = null;
        this.timer = null;
        this.minDwellMs = minDwellMs;
    }
    reset() {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        this.pending = null;
        this.lastShownAt = 0;
    }
    /**
     * Applies a progress payload, either immediately to returned state or via
     * {@link patchCache} after the dwell window when coalescing.
     */
    consume(prev, p, patchCache) {
        const now = Date.now();
        if (this.lastShownAt === 0) {
            this.lastShownAt = now;
            this.pending = null;
            this.clearTimer();
            return applyProgressToBlockStreamState(prev, p);
        }
        if (now - this.lastShownAt >= this.minDwellMs) {
            this.lastShownAt = now;
            this.pending = null;
            this.clearTimer();
            return applyProgressToBlockStreamState(prev, p);
        }
        this.pending = p;
        const waitMs = Math.max(0, this.minDwellMs - (now - this.lastShownAt));
        this.clearTimer();
        this.timer = setTimeout(() => {
            this.timer = null;
            if (!this.pending) {
                return;
            }
            const payload = this.pending;
            this.pending = null;
            this.lastShownAt = Date.now();
            patchCache((qprev) => applyProgressToBlockStreamState(qprev, payload));
        }, waitMs);
        return prev;
    }
    clearTimer() {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
